/**************
 Global
 ***************/

/**************
 Split-theme test attribution
 ***************/

// Stamps the cart with which theme built it, so Shopify Flow can tag the order and the cohort
// stays followable for LTV. `themeTestAttribute` is emitted by layout/theme.liquid (see the
// comment there for why BOTH themes stamp). Guarded so this file still works in any context
// where the global isn't defined.

// Merge our attribute into a cart payload without touching anything else in it.
// Shopify MERGES cart attributes on BOTH /cart/add.js and /cart/update.js — verified on this
// store 2026-08-03 by setting a decoy attribute first and confirming it survived — so this can
// never clobber a key belonging to another app.
function withThemeTestAttribute(formData) {
    if (typeof themeTestAttribute === "undefined" || !themeTestAttribute) {
        return formData;
    }

    return {
        ...formData,
        attributes: {...(formData && formData.attributes), [themeTestAttribute.name]: themeTestAttribute.value}
    };
}

// Re-stamp when the cart disagrees with the theme the shopper is actually on.
//
// Needed because carts outlive themes: the cart cookie survives a cohort switch, and
// /cart/clear.js does NOT clear attributes (verified 2026-08-03 — probe attributes outlived an
// emptied cart), so a stale stamp can persist indefinitely. Re-stamping on mismatch means the
// value always reflects the last theme the shopper actually used before checking out, and it
// also picks up carts created outside this theme's add-to-cart path.
//
// Fires at most one request, only on a genuine mismatch, and only for a cart that has something
// in it — an attribute on an empty cart would mint a cart token for a shopper who never added
// anything.
function reconcileThemeTestAttribute(cart) {
    if (typeof themeTestAttribute === "undefined" || !themeTestAttribute || !cart || !cart.item_count) {
        return Promise.resolve(cart);
    }

    if (cart.attributes && cart.attributes[themeTestAttribute.name] === themeTestAttribute.value) {
        return Promise.resolve(cart);
    }

    return updateCart({attributes: {[themeTestAttribute.name]: themeTestAttribute.value}});
}

//add new product to cart
//let formData = { quantity: 1, id: 794864229, properties: { 'Personalize': 'ZHM' } };
function addProduct(formData) {
    return fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
            'Content-Type': 'application/json'
        },
        // stamped here rather than at the 7 addProduct() call sites — this is the single
        // chokepoint every add-to-cart in the theme goes through, so none can be missed
        body: JSON.stringify(withThemeTestAttribute(formData))
    })
        .then(data => data.json());
}

//change an existing product in your cart
//let formData = { quantity: 1, id: 794864229 } or items: [{ quantity: 1, id: 794864229 }]
function changeCart(formData) {
    return fetch(`${Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
    })
        .then(data => data.json());
}

//update cart note or attributes (_ for attributes are hidden and __ are private)
//let formData = { note: 'This is a note about my order', attributes: { 'Gift wrap': 'Yes' } }
//let formData = {updates: { 794864229: 2, 794864230: 3 }}
function updateCart(formData) {
    return fetch(`${Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
    })
        .then(data => data.json());
}

// download product
function downloadProduct(handle) {
    return fetch(`${Shopify.routes.root}products/${handle}.js`)
        .then(data => data.json());
}

// get cart
function getCart() {
    return fetch(`${Shopify.routes.root}cart.js`)
        .then(data => data.json());
}

// supplemental cart item data (selling plans, compare-at prices), cached for the mini cart render
let cartData = {};

// get supplemental cart item data
function getCartData() {
    return fetch("/cart?view=supplemental-data")
        .then(data => data.json());
}

// refresh the in-memory cart and supplemental data in parallel
async function refreshCart() {
    [cart, cartData] = await Promise.all([getCart(), getCartData()]);
    document.dispatchEvent(new CustomEvent("cart:updated"));
}

// print price (515491 => $5,154.91)
function price(amountInCents, removeTrailingZeros = false) {
    // calculate total
    let total = (parseFloat(amountInCents) / 100).toFixed(2).toString();

    // add commas if needed
    total = total.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // remove trailing zeros
    if (removeTrailingZeros) {
        total = total.toString().replace(".00", "");
    }

    // return total
    return `${currency}${total}`;
}

// sleep
function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**************
 PDP shipping text — mirror the cart's free-shipping milestone
 ***************/

// the free-shipping threshold (in cents) taken from the mini-cart tracker milestones,
// so the PDP text and the cart tracker can never disagree. Prefers the milestone flagged
// `data-free-shipping`; otherwise falls back to the lowest threshold (usually free shipping).
function freeShippingThreshold() {
    // markets without free shipping have no threshold at all. This guard is what stops the
    // fallback below from latching onto the NEXT-lowest milestone once the shipping one stops
    // rendering there — with a gift tier live, that would have the PDP announcing free shipping
    // at the gift's threshold. layout/theme.liquid publishes the flag; anything other than an
    // explicit false means show, matching the fail-open Liquid gate.
    if (window.freeShippingMessagingEnabled === false) {
        return null;
    }
    const milestones = Array.from(document.querySelectorAll(".miniCartHeaderTrackerListItem[data-threshold]"));
    if (milestones.length === 0) {
        return null;
    }
    const flagged = milestones.find(m => m.dataset.freeShipping === "true");
    const source = flagged || milestones.reduce((lowest, m) =>
        parseInt(m.dataset.threshold) < parseInt(lowest.dataset.threshold) ? m : lowest
    );
    const threshold = parseInt(source.dataset.threshold);
    return Number.isNaN(threshold) ? null : threshold;
}

// The amount every threshold is measured against: what the customer will ACTUALLY pay, with any
// managed free gift excluded so a gift can never count toward earning itself. Same figure the
// drawer's checkout button shows.
//
// Deliberately NOT cart.items_subtotal_price. That is the sum of final_line_price, which an
// ORDER-level code discount never touches — verified on this store 2026-08-18 — so a 25% code made
// the progress bar read $83.99 on a cart the customer pays $63.00 for, promising free shipping
// that had not been earned. Every consumer of a threshold reads this one function so the bar, the
// PDP shipping line, the free-gift reconcile and the footer can never disagree.
function qualifyingTotal() {
    if (typeof cart === "undefined" || !cart) {
        return 0;
    }

    let giftTotal = 0;
    (cart.items || []).forEach(item => {
        if (item.properties && item.properties._free_gift) {
            giftTotal += item.final_line_price;
        }
    });

    return Math.max(0, (cart.total_price || 0) - giftTotal);
}

// swap PDP shipping text between the locked and unlocked messages based on the current cart.
// uses qualifyingTotal(), the same figure as theme-mini-cart.js renderTracker(), so the two match.
// the setting reads "<shipping message> | <standing callouts>" (e.g. "Free Shipping £79+ | 1 Year
// Extended Warranty"). only the leading shipping message swaps — everything from the first "|" on
// is still true once shipping unlocks, so it is carried across rather than overwritten.
function renderShippingText() {
    const elements = document.querySelectorAll(".productV1ContentShipping[data-unlocked-text]");
    if (elements.length === 0) {
        return;
    }
    const threshold = freeShippingThreshold();
    const unlocked = threshold !== null && qualifyingTotal() >= threshold;
    elements.forEach(el => {
        const locked = el.dataset.lockedText;
        const separator = locked.indexOf("|");
        const callouts = separator === -1 ? "" : locked.slice(separator).trim();
        const messageEl = el.querySelector(".productV1ContentShippingMessage");

        if (messageEl) {
            const lockedMessage = separator === -1 ? locked : locked.slice(0, separator).trim();
            const message = unlocked ? el.dataset.unlockedText : lockedMessage;
            if (messageEl.textContent !== message) {
                messageEl.textContent = message;
            }
        } else {
            // pre-snippet markup (a section this snippet hasn't been rolled out to): whole line
            const text = unlocked
                ? [el.dataset.unlockedText, callouts].filter(Boolean).join(" ")
                : locked;
            if (el.textContent !== text) {
                el.textContent = text;
            }
        }

        el.classList.toggle("productV1ContentShippingUnlocked", unlocked);
    });
}

/**************
 Warranty tooltip — A/B variant only (Shoplift JS API test)
 ***************/

// SHIPS OFF. layout/theme.liquid adds warrantyTooltipOn to <html> for the assigned variant
// cohort only; in control the PDP shipping line stays exactly as it renders today (plain text,
// no button, no icon) and the <template> holding the tooltip copy is never read.
//
// The variant UPGRADES the phrase rather than the markup shipping hidden, so control has no
// stray button in the accessibility tree announcing an interactive element that does nothing.
const WARRANTY_TOOLTIP_CLASS = "warrantyTooltipOn";

const WARRANTY_TOOLTIP_ICON = '<svg class="pdpWarrantyTooltipToggleIcon" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false"><path fill="currentColor" d="M8 .833A7.167 7.167 0 1 0 8 15.167 7.167 7.167 0 0 0 8 .833Zm0 13.334A6.167 6.167 0 1 1 8 1.833a6.167 6.167 0 0 1 0 12.334ZM7.25 7.25h1.5v4.583h-1.5V7.25Zm0-3h1.5v1.5h-1.5v-1.5Z"/></svg>';

function warrantyTooltipActive() {
    return document.documentElement.classList.contains(WARRANTY_TOOLTIP_CLASS);
}

// turn the plain phrase into a toggle button + popover. Idempotent, and reversible so the
// enable/disable helpers below can flip cohorts during QA on a draft.
function applyWarrantyTooltip() {
    document.querySelectorAll(".pdpWarrantyTooltipPhrase").forEach(phrase => {
        if (phrase.dataset.upgraded) {
            return;
        }
        const line = phrase.closest(".productV1ContentShipping");
        const template = line && line.querySelector(".pdpWarrantyTooltipContent");
        if (!template) {
            return;
        }

        phrase.dataset.upgraded = "1";
        phrase.dataset.plain = phrase.textContent;
        phrase.innerHTML = `<button class="pdpWarrantyTooltipToggle" type="button" aria-expanded="false"><span>${phrase.textContent}</span>${WARRANTY_TOOLTIP_ICON}</button>`;

        const popover = document.createElement("div");
        popover.className = "pdpWarrantyTooltipPopover";
        popover.appendChild(template.content.cloneNode(true));
        line.appendChild(popover);
    });
}

function removeWarrantyTooltip() {
    document.querySelectorAll(".pdpWarrantyTooltipPhrase[data-upgraded]").forEach(phrase => {
        phrase.textContent = phrase.dataset.plain;
        delete phrase.dataset.upgraded;
        delete phrase.dataset.plain;
    });
    document.querySelectorAll(".pdpWarrantyTooltipPopover").forEach(p => p.remove());
}

function closePdpWarrantyTooltips() {
    document.querySelectorAll(".pdpWarrantyTooltipPopover.active").forEach(popover => {
        popover.classList.remove("active");
        const toggle = popover.closest(".productV1ContentShipping").querySelector(".pdpWarrantyTooltipToggle");
        if (toggle) {
            toggle.setAttribute("aria-expanded", "false");
        }
    });
}

// the whole phrase and the icon are one button, so a click on either opens the tooltip.
// delegated from document because the button is created after Shoplift resolves.
document.addEventListener("click", e => {
    const toggle = e.target.closest(".pdpWarrantyTooltipToggle");
    if (!toggle) {
        return;
    }
    const line = toggle.closest(".productV1ContentShipping");
    const popover = line.querySelector(".pdpWarrantyTooltipPopover");
    const willOpen = !popover.classList.contains("active");

    closePdpWarrantyTooltips();

    if (willOpen) {
        popover.classList.add("active");
        toggle.setAttribute("aria-expanded", "true");
    }
});

document.addEventListener("mousedown", e => {
    if (e.target.closest(".productV1ContentShipping")) {
        return;
    }
    closePdpWarrantyTooltips();
});

document.addEventListener("keydown", e => {
    if (e.key === "Escape") {
        closePdpWarrantyTooltips();
    }
});

// Called by the Shoplift gate in layout/theme.liquid, and exposed for manual QA on a draft so
// the cohort can be flipped without a real Shoplift assignment — same convention as
// enableCardHoverPeek / enableCardDescriptionAlt.
window.enableWarrantyTooltip = function () {
    document.documentElement.classList.add(WARRANTY_TOOLTIP_CLASS);
    applyWarrantyTooltip();
    // the cart drawer builds its line items from JS, so re-render if it is already populated
    if (typeof renderItems === "function" && document.querySelector(".miniCartBodyItemsItem")) {
        renderItems();
    }
};

window.disableWarrantyTooltip = function () {
    document.documentElement.classList.remove(WARRANTY_TOOLTIP_CLASS);
    closePdpWarrantyTooltips();
    removeWarrantyTooltip();
    if (typeof renderItems === "function" && document.querySelector(".miniCartBodyItemsItem")) {
        renderItems();
    }
};

// the gate may resolve before or after this file runs; cover the already-assigned case
if (warrantyTooltipActive()) {
    applyWarrantyTooltip();
}

// run on load, then react to every cart change (ATC, cart-drawer edits)
document.addEventListener("cart:updated", renderShippingText);
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", renderShippingText);
} else {
    renderShippingText();
}

/**************
 Design mode open mini cart
 ***************/
if (Shopify.designMode) {
    // if a user clicks on the mini cart
    document.addEventListener('shopify:section:select', function (event) {
        // open mini cart
        if (event.detail.sectionId === 'theme-mini-cart') {
            openCart();
        }

        // if they deselect the mini cart
        if (event.detail.sectionId !== 'theme-mini-cart') {
            closeCart();
        }
    });
}

// scroll to url hash
const maltertechParams = new URLSearchParams(window.location.search);

if (maltertechParams.get("scroll_to_class")) {
    const element = document.querySelector(`.${maltertechParams.get("scroll_to_class")}`);
    if (element) {
        window.scrollTo({
            behavior: 'smooth',
            top: element.offsetTop - 120,
        })
    }
}

if (maltertechParams.get("scroll_to_id")) {
    const element = document.querySelector(`#${maltertechParams.get("scroll_to_id")}`);
    if (element) {
        window.scrollTo({
            behavior: 'smooth',
            top: element.offsetTop - 120,
        })
    }
}

// Function to send back in stock subscription to Klaviyo
async function sendKlaviyoBackInStockRequest(emailAddress, variantID) {
    await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${await klaviyo.account()}`, {
        method: 'POST',
        headers: {
            accept: 'application/vnd.api+json',
            revision: '2024-10-15',
            'content-type': 'application/vnd.api+json'
        },
        body: JSON.stringify({
            data: {
                type: "back-in-stock-subscription",
                attributes: {
                    channels: ["EMAIL"],
                    profile: {
                        data: {
                            type: "profile",
                            attributes: {
                                email: emailAddress,
                            }
                        }
                    }
                },
                relationships: {
                    variant: {
                        data: {
                            type: "catalog-variant",
                            id: `$shopify:::$default:::${variantID}`,
                        }
                    }
                }
            }
        })
    })
}

// upsell swatch click sets that variant to active
document.addEventListener("click", function (e) {
    const swatch = e.target.closest(".upsellItemSwatchesItem");
    if (!swatch) {
        return;
    }

    const card = swatch.closest(".upsellItem");
    if (!card) {
        return;
    }

    // move the active ring to the clicked swatch
    card.querySelectorAll(".upsellItemSwatchesItem.active").forEach(element => element.classList.remove("active"));
    swatch.classList.add("active");

    // retarget the add button at the selected variant
    const addButton = card.querySelector(".upsellItemActionsAdd");
    if (addButton && swatch.dataset.variant) {
        addButton.dataset.variant = swatch.dataset.variant;
    }

    // swap the card image to the variant image when one exists
    const image = card.querySelector(".upsellItemImage");
    if (image && swatch.dataset.imageSrc) {
        image.src = swatch.dataset.imageSrc;
        image.srcset = swatch.dataset.imageSrcset;
        image.alt = swatch.dataset.imageAlt;
    }
});

// collection swatch click sets that variant to active (non-compact cards only)
document.addEventListener("click", function (e) {
    const swatch = e.target.closest(".collectionItemSwatchesItem");
    if (!swatch) {
        return;
    }

    const card = swatch.closest(".collectionItem:not(.compact)");
    if (!card) {
        return;
    }

    // the whole card is a link, so don't navigate when picking a color
    e.preventDefault();

    // read before the ring moves — re-clicking the current color isn't a color change
    const colorChanged = !swatch.classList.contains("active");

    // move the active ring to the clicked swatch
    card.querySelectorAll(".collectionItemSwatchesItem.active").forEach(element => element.classList.remove("active"));
    swatch.classList.add("active");

    // a chosen color owns the card image from here on — the second image belongs to a different
    // color and would contradict the selection, so the hover preview is retired for this card
    if (colorChanged) {
        const hoverMedia = card.querySelector(".collectionItemMedia[data-hover-image]");
        if (hoverMedia) {
            hoverMedia.classList.remove("isHoverActive");
            hoverMedia.dataset.hoverDisabled = "true";
        }
    }

    // retarget the add button at the selected variant
    const addButton = card.querySelector(".collectionItemAdd");
    if (addButton && swatch.dataset.variant) {
        addButton.dataset.variant = swatch.dataset.variant;
    }

    // swap the card image to the variant image when one exists
    const image = card.querySelector(".collectionItemImage");
    if (image && swatch.dataset.imageSrc) {
        image.src = swatch.dataset.imageSrc;
        image.srcset = swatch.dataset.imageSrcset;
        image.alt = swatch.dataset.imageAlt;
    }
});

/******************************************************************************
 Collection card — second-image preview on hover (desktop) / long press (touch)

 Opt-in per section: cards only carry .collectionItemMedia[data-hover-image] when
 the section setting is on and the product has a second image. The preview <img>
 ships with no src, so a disabled or never-hovered card costs zero requests; the
 src is assigned on first activation and the fade waits for the load.
 ******************************************************************************/

// how long a finger must be held on a card before we show the peek — longer than a tap, so a quick
// tap still navigates to the PDP, but short enough that it shows while the user holds-and-scrolls
const CARD_HOVER_PRESS_MS = 250;

// A/B gate. The card markup (data-hover-image) renders whenever the section setting is on, but the
// EFFECT stays off until switched on here — so a Shoplift control cohort sees a normal page while the
// variant runs window.enableCardHoverPeek(). Call enable() unconditionally to ship the effect to 100%.
function cardHoverPeekEnabled() {
    return document.documentElement.classList.contains("cardHoverPeekOn");
}

window.enableCardHoverPeek = function () {
    document.documentElement.classList.add("cardHoverPeekOn");
    initCardHoverPrewarm(); // start warming on-screen cards now that the effect is live
};

window.disableCardHoverPeek = function () {
    document.documentElement.classList.remove("cardHoverPeekOn");
};

function cardHoverMedia(node) {
    // gated: with the effect off, every activation path (desktop hover, touch hold, drag-follow)
    // sees no hover card and does nothing
    if (!cardHoverPeekEnabled() || !node || !node.closest) {
        return null;
    }

    const card = node.closest(".collectionItem:not(.compact)");
    return card ? card.querySelector(".collectionItemMedia[data-hover-image]") : null;
}

// assign the deferred src so the bytes download (and decode) ahead of any reveal.
// no-op once the src is set, so it's safe to call from both prewarm and hover.
function warmCardHoverImage(media) {
    if (!media) {
        return;
    }

    const image = media.querySelector(".collectionItemImageHover");
    if (!image || image.getAttribute("src")) {
        return;
    }

    image.addEventListener("load", () => image.classList.add("isLoaded"), {once: true});
    image.srcset = image.dataset.hoverSrcset || "";
    image.src = image.dataset.hoverSrc;
    if (image.complete && image.naturalWidth) {
        image.classList.add("isLoaded");
    }
}

function showCardHoverImage(media) {
    if (!media || media.dataset.hoverDisabled === "true") {
        return;
    }

    // usually already warmed by the viewport prewarm below; this covers the cold path
    warmCardHoverImage(media);
    media.classList.add("isHoverActive");
}

function hideCardHoverImage(media) {
    if (media) {
        media.classList.remove("isHoverActive");
    }
}

// Pre-warm the hover image for cards in (or near) the viewport so the first hover is instant
// instead of waiting on a cold fetch. Runs after load, during idle time, and the hover <img>
// carries fetchpriority="low" — so these fetches queue behind the page's own images / LCP and
// never compete with initial render. Only cards actually near the viewport are warmed, so a long
// collection doesn't download every second image up front.
const whenIdle = window.requestIdleCallback
    ? cb => window.requestIdleCallback(cb, {timeout: 2000})
    : cb => setTimeout(cb, 200);

const cardHoverPrewarmObserver = "IntersectionObserver" in window
    ? new IntersectionObserver(function (entries, observer) {
        entries.forEach(function (entry) {
            if (!entry.isIntersecting) {
                return;
            }
            const media = entry.target;
            observer.unobserve(media); // one-shot: warm each card once
            whenIdle(function () {
                // a card whose color was changed no longer wants its second image
                if (media.dataset.hoverDisabled !== "true") {
                    warmCardHoverImage(media);
                }
            });
        });
    }, {rootMargin: "200px 0px"})
    : null;

function initCardHoverPrewarm() {
    // gated: the control cohort renders the markup but should never fetch the 2nd images
    if (!cardHoverPrewarmObserver || !cardHoverPeekEnabled()) {
        return;
    }
    document.querySelectorAll(".collectionItemMedia[data-hover-image]").forEach(function (media) {
        cardHoverPrewarmObserver.observe(media);
    });
}

// wait for load so prewarm fetches queue behind the page's own critical images (no-op unless the
// effect is enabled; window.enableCardHoverPeek() re-runs this the moment it switches the effect on)
if (document.readyState === "complete") {
    initCardHoverPrewarm();
} else {
    window.addEventListener("load", initCardHoverPrewarm, {once: true});
}

// mouse only — touch raises pointerover on tap too, and there the long press is the trigger
document.addEventListener("pointerover", function (e) {
    if (e.pointerType !== "mouse") {
        return;
    }

    showCardHoverImage(cardHoverMedia(e.target));
}, {passive: true});

document.addEventListener("pointerout", function (e) {
    if (e.pointerType !== "mouse") {
        return;
    }

    const media = cardHoverMedia(e.target);
    if (!media) {
        return;
    }

    // moving between elements inside the same card is not leaving it
    const card = media.closest(".collectionItem");
    if (card && e.relatedTarget && card.contains(e.relatedTarget)) {
        return;
    }

    hideCardHoverImage(media);
}, {passive: true});

/*********************
 Touch: hold a card to show its 2nd image — while the page keeps scrolling normally

 The point is a mobile "hover": the shopper holds a finger on a card (typically as they scroll) and
 that card's second image shows; the card under the finger updates as the page scrolls past. EVERY
 listener here is passive and NOTHING calls preventDefault — vertical scrolling is never blocked. A
 short hold-timer keeps a quick tap navigating to the PDP as usual; only a held press (which showed a
 peek) has its release click swallowed so a hold doesn't count as a tap.
 *********************/

let cardPressMedia = null;   // the card currently peeked (null when none)
let cardPressTimer = null;
let cardPressActive = false; // the hold has lasted long enough to be a peek, not a tap
let cardPressLastX = 0;
let cardPressLastY = 0;
let cardPressFollowRaf = 0;

// show the peek for whatever hover-enabled card sits under the finger now — a touch only ever targets
// the element it started on, so we hit-test the live coordinates as the page scrolls beneath it
function updateCardPeekUnderFinger() {
    const overMedia = cardHoverMedia(document.elementFromPoint(cardPressLastX, cardPressLastY));
    if (overMedia === cardPressMedia) {
        return;
    }
    hideCardHoverImage(cardPressMedia);
    if (overMedia && overMedia.dataset.hoverDisabled !== "true") {
        cardPressMedia = overMedia;
        showCardHoverImage(cardPressMedia);
    } else {
        // over a gap or a swatch-disabled card — show nothing until the finger finds a card again
        cardPressMedia = null;
    }
}

// coalesce the follow to one hit-test per frame so it can't add jank to the (native) scroll
function scheduleCardPeekFollow() {
    if (cardPressFollowRaf) {
        return;
    }
    cardPressFollowRaf = requestAnimationFrame(function () {
        cardPressFollowRaf = 0;
        if (cardPressActive) {
            updateCardPeekUnderFinger();
        }
    });
}

function endCardPress() {
    if (cardPressTimer) {
        clearTimeout(cardPressTimer);
        cardPressTimer = null;
    }

    if (cardPressActive) {
        // a held press showed a peek — it wasn't a tap, so don't let the release navigate to the PDP.
        // once/self-removing after a beat so a press that ends without a click can't eat the next tap
        const swallowClick = function (event) {
            event.preventDefault();
            event.stopPropagation();
        };
        document.addEventListener("click", swallowClick, {capture: true, once: true});
        setTimeout(() => document.removeEventListener("click", swallowClick, {capture: true}), 400);
    }

    hideCardHoverImage(cardPressMedia);
    cardPressMedia = null;
    cardPressActive = false;
}

document.addEventListener("touchstart", function (e) {
    if (e.touches.length !== 1) {
        return;
    }

    const media = cardHoverMedia(e.target);
    if (!media || media.dataset.hoverDisabled === "true") {
        return;
    }

    cardPressActive = false;
    cardPressLastX = e.touches[0].clientX;
    cardPressLastY = e.touches[0].clientY;
    // after a brief hold, reveal the peek — movement does NOT cancel it, so it shows while scrolling
    cardPressTimer = setTimeout(function () {
        cardPressTimer = null;
        cardPressActive = true;
        updateCardPeekUnderFinger();
    }, CARD_HOVER_PRESS_MS);
}, {passive: true});

// passive: we only READ the finger position and update the peek — the browser scrolls as normal
document.addEventListener("touchmove", function (e) {
    if (!cardPressTimer && !cardPressActive) {
        return;
    }
    if (!e.touches.length) {
        return;
    }
    cardPressLastX = e.touches[0].clientX;
    cardPressLastY = e.touches[0].clientY;
    if (cardPressActive) {
        scheduleCardPeekFollow();
    }
}, {passive: true});

document.addEventListener("touchend", endCardPress);
document.addEventListener("touchcancel", endCardPress);

// Android fires contextmenu on a long press; iOS is handled by -webkit-touch-callout in CSS
document.addEventListener("contextmenu", function (e) {
    if (cardPressActive && cardHoverMedia(e.target)) {
        e.preventDefault();
    }
});

// card ATC buttons- .collectionItemAdd lives inside a link, so stop the click from navigating
document.addEventListener("click", async function (e) {
    const button = e.target.closest(".collectionItemAdd, .upsellItemActionsAdd");
    if (!button) {
        return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (button.disabled) {
        return;
    }

    const variantID = parseInt(button.dataset.variant);
    if (!variantID) {
        return;
    }

    button.disabled = true;
    button.classList.add("loading");

    try {
        await addProduct({items: [{quantity: 1, id: variantID}]});

        if (typeof openCart === "function") {
            await openCart();
        }
    } finally {
        button.disabled = false;
        button.classList.remove("loading");
    }
});